import styles from "./ConfirmationPanel.module.scss";

import { useCallback, useMemo } from "react";

import { useRouter } from "@tanstack/react-router";
import { formatInTimeZone } from "date-fns-tz";
import { useTranslation } from "react-i18next";

import { AppButton } from "@/modules/shared/components/AppButton";
import { isInNativeWebView } from "@/modules/shared/utils/isInNativeWebView";

import { BookingKinds, useBookingContext } from "../../context/BookingContext";
import { CalendarIcon } from "../../svgs/CalendarIcon";
import { CheckCircleIcon } from "../../svgs/CheckCircleIcon";
import { ClockIcon } from "../../svgs/ClockIcon";
import { GlobeIcon } from "../../svgs/GlobeIcon";
import { VideoIcon } from "../../svgs/VideoIcon";
import { sendNativeMessage } from "../../utils/sendNativeMessage";
import { GoogleMeetInstallNote } from "../GoogleMeetInstallNote";

export const ConfirmationPanel = ({
  compact = false,
}: {
  compact?: boolean;
}) => {
  const { t } = useTranslation();
  const router = useRouter();

  const {
    coachName,
    timezone,
    selectedDateTime,
    cleanBookingState,
    completedSessionData,
    bookingKind,
    multiSessionBookingEnabled,
    isRescheduling,
  } = useBookingContext();

  const isCoachMatchingServiceBooking =
    bookingKind === BookingKinds.COACH_MATCHING_SERVICE;

  const showBookMoreSessions =
    isInNativeWebView() &&
    multiSessionBookingEnabled &&
    !isCoachMatchingServiceBooking &&
    !isRescheduling;

  const formattedDateTime = useMemo(() => {
    if (!selectedDateTime || !timezone) return "";

    const date = new Date(selectedDateTime);
    const datePart = formatInTimeZone(date, timezone.value, "EEEE, MMMM d");
    const timePart = formatInTimeZone(
      date,
      timezone.value,
      "h:mma",
    ).toLowerCase();

    return t("coachingSession.bookingConfirmation.sessionDateTime", {
      date: datePart,
      time: timePart,
    });
  }, [selectedDateTime, timezone, t]);

  const handleBookAnotherClick = useCallback(() => {
    cleanBookingState();
  }, [cleanBookingState]);

  const handleBookMoreSessionsClick = useCallback(() => {
    if (!isInNativeWebView()) return;
    sendNativeMessage({
      eventName: "BOOK_MORE_SESSIONS",
      sessionStartTime: completedSessionData?.sessionStartTime,
    });
  }, [completedSessionData?.sessionStartTime]);

  const handleDoneClick = useCallback(() => {
    if (isInNativeWebView()) {
      sendNativeMessage({ eventName: "BOOKING_REDIRECT" });
      return;
    }
    cleanBookingState();
    if (router.history.canGoBack()) {
      router.history.back();
    }
  }, [cleanBookingState, router]);

  return (
    <div
      className={`${styles.confirmationContainer} ${compact ? styles.compact : ""}`}
    >
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <CheckCircleIcon className={styles.checkIcon} />
          <h1 className={styles.title}>
            {t("coachingSession.bookingConfirmation.title")}
          </h1>
        </div>
        <p className={styles.subtitle}>
          {t("coachingSession.bookingConfirmation.invitationSent")}
        </p>
      </div>

      <div className={styles.card}>
        <div className={styles.sessionInfo}>
          <div className={styles.sessionType}>
            {isCoachMatchingServiceBooking
              ? t(
                  "coachingSession.bookingConfirmation.coachMatchingSessionType",
                )
              : t("coachingSession.bookingConfirmation.sessionType")}
          </div>
          <div className={styles.coachName}>
            {isCoachMatchingServiceBooking
              ? t("bookingWidget.coachMatchingService.title")
              : t("bookingWidget.coachLabel", { name: coachName })}
          </div>
        </div>

        <div className={styles.details}>
          <div className={styles.detailRow}>
            <CalendarIcon className={styles.icon} />
            <span className={styles.detailText}>{formattedDateTime}</span>
          </div>

          <div className={styles.detailRow}>
            <GlobeIcon className={styles.icon} />
            <span className={styles.detailText}>{timezone?.label}</span>
          </div>

          <div className={styles.detailRow}>
            <ClockIcon className={styles.icon} />
            <span className={styles.detailText}>
              {t("bookingWidget.sessionDuration")}
            </span>
          </div>

          <div className={styles.detailRow}>
            <VideoIcon className={styles.icon} />
            <span className={styles.detailText}>
              {t("bookingWidget.sessionDescription")}
            </span>
          </div>
        </div>
      </div>

      <GoogleMeetInstallNote />

      <div className={styles.buttonGroup}>
        {!isCoachMatchingServiceBooking && !showBookMoreSessions && (
          <AppButton
            onClick={handleBookAnotherClick}
            variant="primary"
            className={styles.primaryButton}
          >
            {t("coachingSession.bookingConfirmation.cta")}
          </AppButton>
        )}
        {showBookMoreSessions && (
          <AppButton
            onClick={handleBookMoreSessionsClick}
            variant="primary"
            className={styles.primaryButton}
          >
            {t("coachingSession.bookingConfirmation.bookMoreSessionsCta")}
          </AppButton>
        )}
        <AppButton
          onClick={handleDoneClick}
          variant="primary"
          className={styles.doneButton}
        >
          {t("coachingSession.bookingConfirmation.doneCta")}
        </AppButton>
      </div>
    </div>
  );
};
